/**
 * Device Fingerprinting Utility
 * Generates a unique fingerprint for the user's device/browser
 * Used for single-device login restriction
 */

import FingerprintJS from '@fingerprintjs/fingerprintjs';

let fpPromise = null;

/**
 * Initialize the fingerprinting library
 * Should be called once when the app loads
 */
export const initFingerprint = async () => {
  if (!fpPromise) {
    fpPromise = FingerprintJS.load();
  }
  return fpPromise;
};

/**
 * Get the device fingerprint
 * @returns {Promise<string>} Device fingerprint hash
 */
export const getDeviceFingerprint = async () => {
  try {
    // Initialize if not already done
    if (!fpPromise) {
      await initFingerprint();
    }

    const fp = await fpPromise;
    const result = await fp.get();
    
    // The visitorId is the unique fingerprint
    return result.visitorId;
  } catch (error) {
    console.error('Error generating device fingerprint:', error);
    // Fallback to a basic fingerprint if FingerprintJS fails
    return generateBasicFingerprint();
  }
};

/**
 * Fallback: Generate a basic fingerprint using browser properties
 * Used if FingerprintJS fails
 */
const generateBasicFingerprint = () => {
  const navigator = window.navigator;
  const screen = window.screen;
  
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.colorDepth,
    screen.width + 'x' + screen.height,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 'unknown',
    navigator.deviceMemory || 'unknown'
  ];
  
  // Simple hash function
  const str = components.join('|');
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  return Math.abs(hash).toString(36);
};

/**
 * Get device information for display/logging purposes
 * @returns {object} Device information
 */
export const getDeviceInfo = () => {
  const navigator = window.navigator;
  const screen = window.screen;
  
  // Parse user agent to get browser and OS
  const ua = navigator.userAgent;
  let browserName = 'Unknown';
  let osName = 'Unknown';
  
  // Detect browser
  if (ua.indexOf('Chrome') > -1 && ua.indexOf('Edg') === -1) {
    browserName = 'Chrome';
  } else if (ua.indexOf('Safari') > -1 && ua.indexOf('Chrome') === -1) {
    browserName = 'Safari';
  } else if (ua.indexOf('Firefox') > -1) {
    browserName = 'Firefox';
  } else if (ua.indexOf('Edg') > -1) {
    browserName = 'Edge';
  } else if (ua.indexOf('MSIE') > -1 || ua.indexOf('Trident') > -1) {
    browserName = 'Internet Explorer';
  }
  
  // Detect OS
  if (ua.indexOf('Win') > -1) {
    osName = 'Windows';
  } else if (ua.indexOf('Mac') > -1) {
    osName = 'MacOS';
  } else if (ua.indexOf('Linux') > -1) {
    osName = 'Linux';
  } else if (ua.indexOf('Android') > -1) {
    osName = 'Android';
  } else if (ua.indexOf('iOS') > -1 || ua.indexOf('iPhone') > -1 || ua.indexOf('iPad') > -1) {
    osName = 'iOS';
  }
  
  return {
    browser: browserName,
    os: osName,
    screenResolution: `${screen.width}x${screen.height}`,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };
};
